import { z } from 'zod';

export const AdminCreateChapterSchema = z.object({
  title: z.string().min(1, 'Title is required').max(300),
  subtitle: z.string().trim().max(50).optional(),
  index: z.number().int().min(1),
  isDemo: z.boolean().default(false),
});

export type AdminCreateChapterDto = z.infer<typeof AdminCreateChapterSchema>;

export const AdminUpdateChapterSchema = z.object({
  title: z.string().min(1).max(300).optional(),
  subtitle: z.string().trim().max(50).optional(),
  index: z.number().int().min(1).optional(),
  isDemo: z.boolean().optional(),
  visibility: z.enum(['draft', 'published', 'hidden']).optional(),
});

export type AdminUpdateChapterDto = z.infer<typeof AdminUpdateChapterSchema>;

export const AdminReorderChaptersSchema = z.object({
  order: z
    .array(z.object({ id: z.string(), index: z.number().int().min(1) }))
    .min(1, 'At least one item required'),
});

export type AdminReorderChaptersDto = z.infer<typeof AdminReorderChaptersSchema>;

export const AdminDuplicateChapterResultSchema = z.object({
  id: z.string(),
  clubId: z.string(),
  index: z.number(),
  title: z.string(),
  subtitle: z.string().nullable(),
  isDemo: z.boolean(),
  visibility: z.enum(['draft', 'published', 'hidden']),
  publishedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  _count: z.object({ blocks: z.number() }).optional(),
});

export type AdminDuplicateChapterResult = z.infer<typeof AdminDuplicateChapterResultSchema>;

const AdminBulkMoveChaptersIdsSchema = z.array(z.string().uuid()).min(1).max(100).refine(
  (chapterIds) => new Set(chapterIds).size === chapterIds.length,
  'Chapter IDs must be unique',
);

export const AdminBulkChaptersIdsSchema = AdminBulkMoveChaptersIdsSchema;

export const AdminBulkMoveChaptersSchema = z.object({
  chapterIds: AdminBulkMoveChaptersIdsSchema,
  targetClubId: z.string().uuid(),
});

export type AdminBulkMoveChaptersDto = z.infer<typeof AdminBulkMoveChaptersSchema>;

export const AdminBulkChapterActionSchema = z.discriminatedUnion('action', [
  z.object({
    action: z.literal('move'),
    chapterIds: AdminBulkMoveChaptersIdsSchema,
    targetClubId: z.string().uuid(),
  }),
  z.object({
    action: z.literal('hide'),
    chapterIds: AdminBulkMoveChaptersIdsSchema,
  }),
  z.object({
    action: z.literal('show'),
    chapterIds: AdminBulkMoveChaptersIdsSchema,
  }),
  z.object({
    action: z.literal('delete'),
    chapterIds: AdminBulkMoveChaptersIdsSchema,
  }),
]);

export type AdminBulkChapterActionDto = z.infer<typeof AdminBulkChapterActionSchema>;

export const AdminBulkMoveChaptersResultSchema = z.object({
  action: z.literal('move'),
  sourceClubId: z.string().uuid(),
  targetClubId: z.string().uuid(),
  chapterIds: z.array(z.string().uuid()),
  affectedCount: z.number().int().min(0),
});

export type AdminBulkMoveChaptersResult = z.infer<typeof AdminBulkMoveChaptersResultSchema>;

export const AdminBulkChapterActionResultSchema = z.object({
  action: z.enum(['move', 'hide', 'show', 'delete']),
  sourceClubId: z.string().uuid(),
  targetClubId: z.string().uuid().optional(),
  chapterIds: z.array(z.string().uuid()),
  affectedCount: z.number().int().min(0),
});

export type AdminBulkChapterActionResult = z.infer<typeof AdminBulkChapterActionResultSchema>;

export const AdminChapterResponseSchema = z.object({
  id: z.string(),
  clubId: z.string(),
  index: z.number(),
  title: z.string(),
  subtitle: z.string().nullable(),
  isDemo: z.boolean(),
  visibility: z.enum(['draft', 'published', 'hidden']),
  publishedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  _count: z.object({ blocks: z.number() }).optional(),
});

export type AdminChapterResponse = z.infer<typeof AdminChapterResponseSchema>;
